import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Check, ArrowRight, Users, Shield, Zap, FileDown, FileUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Presentation only — prices, user bands and labels come from /api/pricing-tiers
// so the marketing page always mirrors what billing actually charges.
const TIER_STYLE = {
  bronze: {
    color: 'from-amber-700 to-amber-600',
    icon: Shield,
    features: ['Job scheduling & Gantt chart', 'Technician assignments', 'Cost reports', 'PDF/Word exports', 'Fillable form templates'],
  },
  silver: {
    color: 'from-slate-500 to-slate-400',
    icon: Users,
    popular: true,
    features: ['Everything in Bronze', 'Field dispatch calendar', 'Advanced margin reports', 'MS Project import & export', 'Priority support'],
  },
  gold: {
    color: 'from-yellow-500 to-amber-500',
    icon: Zap,
    features: ['Everything in Silver', 'Unlimited team members', 'Full analytics suite', 'Custom form templates', 'Dedicated support'],
  },
};

const TIER_ORDER = ['bronze', 'silver', 'gold'];

const userBand = (tier) => {
  const min = tier.min_users ?? 1;
  const max = tier.max_users ?? 0;
  if (!max || max >= 999) return 'Unlimited users';
  return `${min} – ${max} users`;
};

const money = (n) => (Number(n) % 1 === 0 ? Number(n).toFixed(0) : Number(n).toFixed(2));

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tiers, setTiers] = useState(null);
  const [pricingError, setPricingError] = useState(false);

  const loadPricing = useCallback(() => {
    setPricingError(false);
    axios.get(`${API}/pricing-tiers`)
      .then((res) => setTiers(res.data))
      .catch(() => setPricingError(true));
  }, []);

  useEffect(() => { loadPricing(); }, [loadPricing]);

  // /features and /pricing are real URLs (declared in sitemap.xml) that land on
  // the matching section of this page.
  useEffect(() => {
    const target = { '/features': 'features', '/pricing': 'pricing', '/showcase': 'showcase' }[location.pathname];
    if (!target) return;
    const t = setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' }), 250);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const planCards = useMemo(() => {
    if (!tiers) return [];
    return TIER_ORDER.filter((key) => tiers[key]).map((key) => ({
      key,
      name: tiers[key].label || key,
      annual: tiers[key].annual_fee,
      perUser: tiers[key].per_user_monthly,
      users: userBand(tiers[key]),
      ...TIER_STYLE[key],
    }));
  }, [tiers]);

  const goPricing = () => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Header */}
      <header className="border-b border-white/10" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <div className="flex min-w-0 items-center gap-3" data-testid="landing-header-brand">
            <img
              src="/logo.png"
              alt="ShopManager247 logo — repair shop management software for industrial and mechanical shops"
              className="h-10 w-10 rounded-lg"
            />
            <span className="text-xl font-bold text-white tracking-tight">ShopManager<span className="text-orange-400">247</span></span>
          </div>
          <div className="grid w-full grid-cols-2 items-center gap-3 sm:flex sm:w-auto" data-testid="landing-header-actions">
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-slate-300 hover:text-white hover:bg-white/10"
              data-testid="header-login-btn"
            >
              Log In
            </Button>
            <Button
              onClick={goPricing}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold"
              data-testid="header-signup-btn"
            >
              Start Free Trial
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-16 text-center">
        <div className="mb-8">
          <img
            src="/logo.png"
            alt="ShopManager247 — Gantt job scheduling, technician time tracking and margin reporting for repair shops"
            className="h-28 w-28 mx-auto rounded-2xl shadow-2xl shadow-blue-500/20"
          />
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
          Repair Shop Management Software,<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">Built for Industrial Shops</span>
        </h1>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
          Gantt-style job scheduling, multi-status workflow, technician time tracking, and margin reporting — purpose-built for industrial and mechanical repair shops.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Button
            size="lg"
            onClick={goPricing}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 h-12 text-base"
            data-testid="hero-get-started-btn"
          >
            Start Free Trial <ArrowRight size={18} className="ml-2" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => navigate('/login')}
            className="border-slate-600 text-slate-300 hover:bg-white/5 hover:text-white px-8 h-12 text-base"
            data-testid="hero-login-btn"
          >
            Log In
          </Button>
        </div>
        <p className="text-sm text-slate-500 mt-4">1-month free trial on all plans. No credit card required.</p>
      </section>

      {/* Product showcase — real screenshots */}
      <section id="showcase" className="max-w-7xl mx-auto px-6 py-16" data-testid="showcase-section">
        <h2 className="text-center text-3xl sm:text-4xl font-black text-white mb-3">See Your Whole Shop on One Timeline</h2>
        <p className="text-center text-slate-400 mb-14 max-w-2xl mx-auto">
          Real screens from ShopManager247 — not mockups. Every job, phase and dollar in one place.
        </p>

        <div className="space-y-20">
          {/* Gantt */}
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-orange-400 mb-3">Gantt Job Scheduling</span>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Drag-and-drop timeline, phase by phase</h3>
              <p className="text-slate-400 leading-relaxed mb-4">
                Every active job sits on one Gantt timeline in day, week or month scale. Expand a job and its
                workflow phases — intake, teardown, quote, parts, machining, reassembly — fan out as colour-coded
                bars with their own dates and finish-to-start dependencies, so a slipped parts delivery visibly
                pushes the phases behind it.
              </p>
              <ul className="space-y-2 text-sm text-slate-300">
                {['Day / week / month zoom with a live "today" marker',
                  'Per-phase segments with dependency links',
                  'Filter by customer, status or technician',
                  'Click any bar to open the job and its costs'].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Check size={16} className="text-orange-400 mt-0.5 flex-shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-3">
              <img
                src="/marketing/gantt-schedule.png"
                alt="Screenshot of the ShopManager247 Gantt schedule showing six industrial repair jobs on a day-scale timeline, with job J-24118 expanded into colour-coded intake, teardown, quote, parts, machining and reassembly phase bars"
                width="1632"
                height="740"
                loading="lazy"
                className="w-full rounded-xl border border-white/10 shadow-2xl shadow-black/40"
                data-testid="showcase-gantt-img"
              />
            </div>
          </div>

          {/* Reporting */}
          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3 order-2 lg:order-1">
              <img
                src="/marketing/margin-reporting.png"
                alt="Screenshot of the ShopManager247 margin report ranking customers by revenue, labour cost, profit and average margin percentage, with tabs for job cost, technician, model, customer, maintenance and compliance reporting"
                width="1632"
                height="610"
                loading="lazy"
                className="w-full rounded-xl border border-white/10 shadow-2xl shadow-black/40"
                data-testid="showcase-reports-img"
              />
            </div>
            <div className="lg:col-span-2 order-1 lg:order-2">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-orange-400 mb-3">Costing &amp; Margin Reporting</span>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Know the margin before the invoice goes out</h3>
              <p className="text-slate-400 leading-relaxed mb-4">
                Logged hours, overtime multipliers and equipment run-time roll straight into job cost and margin
                reports. Compare profitability by job, technician, equipment model or customer, spot the jobs
                quietly losing money, and export the numbers to PDF or Word for the customer package.
              </p>
              <ul className="space-y-2 text-sm text-slate-300">
                {['Job cost breakdown: labour, overtime, equipment',
                  'Margin by technician, model and customer',
                  'Maintenance and compliance reporting built in',
                  'One-click PDF / Word export with forms appended'].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Check size={16} className="text-orange-400 mt-0.5 flex-shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MS Project interop */}
      <section className="max-w-7xl mx-auto px-6 py-10" data-testid="msproject-section">
        <div className="rounded-2xl border border-orange-500/30 bg-gradient-to-br from-[#12203a] to-[#0d1830] p-8 sm:p-12">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-orange-400 mb-3">Microsoft Project Compatible</span>
          <h2 className="text-2xl sm:text-4xl font-black text-white mb-4 max-w-4xl">
            Import and export Microsoft Project files — keep the schedule your customer already asked for
          </h2>
          <p className="text-slate-400 max-w-4xl leading-relaxed mb-8">
            Upload a Microsoft Project XML schedule and ShopManager247 turns its tasks, durations and
            finish-to-start links into a reusable shop schedule template you can apply to any job in seconds.
            Build or adjust the plan in the shop, then export any job back out as an MS Project XML file for
            project managers, turnaround planners and clients who work in MS Project — no re-typing a schedule
            into two systems.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-black/20 border border-white/10 p-5">
              <FileUp size={20} className="text-orange-400 mb-3" />
              <h3 className="text-white font-bold mb-1.5">Import .xml schedules</h3>
              <p className="text-sm text-slate-400">
                Tasks, durations and dependencies become phase plans and saved templates — apply the same standard
                plan to every overhaul of that equipment type.
              </p>
            </div>
            <div className="rounded-xl bg-black/20 border border-white/10 p-5">
              <FileDown size={20} className="text-orange-400 mb-3" />
              <h3 className="text-white font-bold mb-1.5">Export back to MS Project</h3>
              <p className="text-sm text-slate-400">
                Send a job&apos;s live phase schedule out as MS Project XML so planners and customers see progress in
                the tool they already use.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features (keyword-rich for SEO + conversion) */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-center text-3xl sm:text-4xl font-black text-white mb-3">
          Everything an Industrial Repair Shop Needs
        </h2>
        <p className="text-center text-slate-400 mb-14 max-w-2xl mx-auto">
          One platform for job scheduling, technician tracking, cost reporting, and PDF forms — no more juggling spreadsheets, whiteboards, and email threads.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              h: 'Gantt-Style Job Scheduling',
              p: 'See every active job on a timeline with expandable per-status segments — intake, teardown, quote, parts, machining, reassembly — drag to reschedule.'
            },
            {
              h: 'Microsoft Project Import & Export',
              p: 'Bring MS Project XML schedules in as reusable shop templates with their task dependencies, and export any job back out as MS Project XML for planners and clients.'
            },
            {
              h: 'Multi-Status Job Tracking',
              p: 'Move jobs through customisable workflow states with full status history. Hide defaults or add custom statuses for your shop.'
            },
            {
              h: 'Technician Time & Overtime',
              p: 'Log hours per assignment and per general shop-time category. Overtime multipliers flow automatically into the cost and margin reports.'
            },
            {
              h: 'Margin & Profitability Reports',
              p: 'Standard cost reports plus advanced margin reporting by Technician, by Model, and by Customer — including overtime and shop overhead.'
            },
            {
              h: 'Fillable PDF Forms & Exports',
              p: 'Upload fillable PDF templates, capture readings inline, attach to jobs, and export the whole job package as a single PDF with the forms appended.'
            },
            {
              h: 'Field Dispatch Calendar',
              p: 'Dispatch field crews on a continuous-scrolling calendar, colour-coded per technician, with equipment lists and site attachments on every event.'
            },
            {
              h: 'Customer & Equipment Management',
              p: 'Track customers, locations, LSDs, and multiple pieces of equipment per job with serial numbers and full repair history.'
            },
            {
              h: 'Roles, Permissions & Compliance',
              p: 'Management, foreman, shop tech, field crew and admin/safety roles with granular permissions, plus maintenance and compliance reporting.'
            },
          ].map((f) => (
            <div key={f.h} className="rounded-xl bg-slate-900/60 border border-slate-800 p-6">
              <h3 className="text-white font-bold text-lg mb-2">{f.h}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{f.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-center text-3xl sm:text-4xl font-black text-white mb-3">
          Simple, Transparent Pricing
        </h2>
        <p className="text-center text-slate-400 mb-14 max-w-xl mx-auto">
          Choose the plan that fits your shop. Every plan includes a 1-month free trial.
        </p>

        {pricingError ? (
          <div className="text-center" data-testid="pricing-error">
            <p className="text-slate-400 mb-4">We couldn&apos;t load current pricing just now.</p>
            <Button onClick={loadPricing} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold" data-testid="pricing-retry-btn">
              Retry
            </Button>
          </div>
        ) : !tiers ? (
          <p className="text-center text-slate-500" data-testid="pricing-loading">Loading current pricing…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {planCards.map((tier) => {
              const Icon = tier.icon;
              return (
                <Card
                  key={tier.key}
                  className={`relative bg-[#111d33] border ${tier.popular ? 'border-orange-500/50 shadow-xl shadow-orange-500/10' : 'border-white/10'} rounded-xl overflow-hidden`}
                  data-testid={`tier-card-${tier.key}`}
                >
                  {tier.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-orange-500 to-yellow-500 text-center py-1.5 text-xs font-bold text-white uppercase tracking-wider">
                      Most Popular
                    </div>
                  )}
                  <div className={`p-6 ${tier.popular ? 'pt-10' : ''}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`bg-gradient-to-br ${tier.color} p-2 rounded-lg`}>
                        <Icon size={20} className="text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                    </div>

                    <div className="mb-1">
                      <span className="text-4xl font-black text-white" data-testid={`tier-price-${tier.key}`}>${money(tier.annual)}</span>
                      <span className="text-slate-400 text-sm">/year</span>
                    </div>
                    <p className="text-sm text-slate-400 mb-1" data-testid={`tier-peruser-${tier.key}`}>
                      + ${money(tier.perUser)}/user/month
                    </p>
                    <p className="text-xs text-orange-400 font-semibold mb-6">{tier.users}</p>

                    <Button
                      onClick={() => navigate(`/login?register=true&tier=${tier.key}`)}
                      className={`w-full font-semibold h-11 ${tier.popular ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-white/10 hover:bg-white/15 text-white'}`}
                      data-testid={`select-tier-${tier.key}`}
                    >
                      Start Free Trial
                    </Button>

                    <ul className="mt-6 space-y-3">
                      {tier.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                          <Check size={16} className="text-orange-400 mt-0.5 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="ShopManager247 logo" className="h-6 w-6 rounded" />
            <span className="text-sm text-slate-500">ShopManager247</span>
          </div>
          <nav className="flex items-center gap-5 text-sm text-slate-500">
            <a href="/features" className="hover:text-slate-300">Features</a>
            <a href="/pricing" className="hover:text-slate-300">Pricing</a>
            <a href="/about" className="hover:text-slate-300">About</a>
            <a href="/login" className="hover:text-slate-300">Log In</a>
          </nav>
          <p className="text-sm text-slate-600">&copy; {new Date().getFullYear()} ShopManager247. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
